// A setting the switch's pinned schema has no key for, sent as one command-line
// instruction instead of a configuration document (ADR-0417, #1850). Today that is a
// read-only SNMP community, the switch's on-box SNMP agent (enable or disable), its DNS lookup and
// default domain name (#1870), and remote logging's facility (#1869), each its own control in this
// one section and each measured on one switch model and firmware. **A remote syslog host and the
// switch's name servers are not here** (ADR-0444, amending ADR-0417's scope): the switch's pinned
// schema states them, so they are switch settings in the plan above (SwitchServicesEditor).
//
// **This is not the Save, Plan, Apply path and is drawn apart from it.** There is no
// plan and no preview, because a command line is not previewable; the result is what the
// switch showed when it was read back. Design's alone: the write is handed in by the
// page with the rest of the port writes, and a device's page in Operate offers none.
//
// **The community name is secret material** (ADR-0224). It is held in this form's state
// until it is sent and cleared the moment it is, kept nowhere else, and never shown: the
// answer does not carry it, and nothing here repeats it. The SNMP agent control takes no
// name and sends none.
//
// **The path-cost control is reach-affecting, and drawn to say so** (#1874, ADR-0426). A port's
// spanning-tree path cost can move the switch's root path, so unlike the others it is guarded against the
// switch's uplink port (refused) and is kept in running configuration only, never saved, so it reverts at
// the next restart — the safe direction, since a saved bad cost would outlive a reboot. The note beside it
// and a confirmed change's answer both say so, so "set" is never read as "saved and permanent".
//
// **Remote logging's facility, DNS lookup and the default domain name are saved like a community**
// (#1869, #1870): no key in the switch's pinned schema states any of them.
//
// **Which settings this switch admits is read, never worked out here** (#1868). The server
// says it from the same table the write's gate refuses on (`GET …/cli-settings`), and this
// section only draws it: a setting the read leaves out is drawn disabled with the reason up
// front. That is a hint and never the gate. While the read is pending, or if it fails, nothing
// is claimed and the controls stay as they were, because the write's own gate is
// authoritative — its refusal is the apply's record's since ADR-0480 (#2061) — and the
// copy below stays as the fallback. There is no list of models or
// firmwares in this file, and there must not be: a second copy of the rule is the defect.
// **A direction refused on every switch is read the same way** (ADR-0521): the read's
// `withdrawn` names it with why, and that choice is drawn disabled with the server's words.
// Today it is DNS lookup's disable, which was measured turning itself back on.
//
// **A refusal is shown and a write is never repeated for the operator.** A send that got
// no answer in time may have taken effect, so the section says to read the switch before
// asking again, and no path here resends.
import { useState } from "react";
import { SKIP, useQuery } from "../../api/hooks";
import type { components } from "../../api/schema";
import type { useMutation } from "../../api/mutation";
import { AcceptedApplyNotice, useAcceptedApply } from "../../shell/AcceptedApply";
import { Card, Info, ProblemBox } from "../../ui";
import { listed, unrecognized } from "../../api/openEnum";

type Request = components["schemas"]["SwitchCliSettingApplyRequest"];
type Applied = components["schemas"]["SwitchCliSettingApplied"];
type Admission = components["schemas"]["SwitchCliSettingsAdmission"];

/** The write, handed in by the page. It answers `202` with the apply's id (ADR-0480, #2061), and what
 * the switch showed is the apply's record, whose `result` on `done` is `SwitchCliSettingApplied`. */
export type ApplyCliSetting = (serial: string, body: Request) => Promise<{ data?: components["schemas"]["ApplyAccepted"]; error?: unknown; response: Response }>;

/** The admission read, handed in by the page beside the write (Design's alone). */
export type ReadCliAdmission = (serial: string) => Promise<{ data?: Admission; error?: unknown; response: Response }>;

const COMMUNITY = /^[A-Za-z0-9_-]{1,32}$/;
/** A dotted-quad IPv4, the shape of the contract's route fields. */
const IPV4 = /^((25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])\.){3}(25[0-5]|2[0-4][0-9]|1[0-9]{2}|[1-9]?[0-9])$/;

/** The ethernet port of a static mrouter port, `1/<n>`; the server refuses a port that does not exist. */
const MROUTER_PORT = /^1\/[0-9]{1,2}$/;
/** A static mrouter port's VLAN id, 1..4094; narrower than the digits so a bad id is caught here. */
function mrouterVlanValid(v: string): boolean {
  return /^[0-9]{1,4}$/.test(v) && Number(v) >= 1 && Number(v) <= 4094;
}

/** The ethernet port whose path cost is set, `1/<n>`; the server refuses a port that does not exist. */
const PATH_COST_PORT = /^1\/[0-9]{1,2}$/;
/** A spanning-tree path cost, 1..200000000 (IEEE 802.1t long-method ceiling); a bad value is caught here. */
function pathCostValueValid(v: string): boolean {
  return /^[0-9]{1,9}$/.test(v) && Number(v) >= 1 && Number(v) <= 200000000;
}

/** The note drawn beside the path-cost control, always. Path cost is a reach-affecting spanning-tree change. */
const PATH_COST_NOTE = "Sets one port's spanning-tree path cost. Zenith refuses the switch's uplink port (a cost there could move the root path and strand the switch), and the change is kept in running configuration only, not saved, so it reverts at the next restart.";

/** A dotted-quad IPv4, the shape of the contract's route fields; the server does the exact validation (aligned
 * prefix, contiguous mask, unicast next hop) and the reachability refusal. This only holds back what cannot be one. */
const ROUTE_ADDR = IPV4;

/** The note drawn beside the static-route control, always. A route can change the switch's own path to its cell. */
const STATIC_ROUTE_NOTE = "Sets a static route on the switch. Zenith refuses the default route and any route that could change how the switch reaches its cell (one covering the gateway or overlapping a directly-connected network), and refuses everything if the switch's route table cannot be read. The change is kept in running configuration only, not saved, so it reverts at the next restart.";

/** A default domain name: lowercase DNS labels, at most 63 characters, the contract's `domain_name`. */
const DOMAIN_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?(\.[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?)*$/;
function domainNameValid(v: string): boolean {
  return v.length >= 1 && v.length <= 63 && DOMAIN_NAME.test(v);
}

/** How the answer names each setting: the subject, the noun for a refusal, and the verbs for set and unset. */
const WORDS: Record<components["schemas"]["SwitchCliSettingAppliedSetting"], { subject: string; thing: string; set: string; unset: string; wantSet: string; wantUnset: string }> = {
  snmp_community: { subject: "The community", thing: "community", set: "added", unset: "removed", wantSet: "present", wantUnset: "removed" },
  snmp_agent: { subject: "The SNMP agent", thing: "SNMP agent", set: "enabled", unset: "disabled", wantSet: "enabled", wantUnset: "disabled" },
  mrouter_port: { subject: "The mrouter port", thing: "mrouter port", set: "added", unset: "removed", wantSet: "listed", wantUnset: "removed" },
  path_cost: { subject: "The path cost", thing: "path cost", set: "set", unset: "removed", wantSet: "set to that cost", wantUnset: "removed" },
  static_route: { subject: "The static route", thing: "static route", set: "added", unset: "removed", wantSet: "listed", wantUnset: "removed" },
  syslog_facility: { subject: "The syslog facility", thing: "syslog facility", set: "set", unset: "reset to the default", wantSet: "at that facility", wantUnset: "back at the default" },
  dns_domain_lookup: { subject: "DNS lookup", thing: "DNS lookup", set: "enabled", unset: "disabled", wantSet: "enabled", wantUnset: "disabled" },
  dns_domain_name: { subject: "The default domain name", thing: "default domain name", set: "set", unset: "cleared", wantSet: "set to that name", wantUnset: "cleared" },
};

/** What the answer says, in the operator's words. Pure, for the test. */
export function describeApplied(a: Applied): { tone: "ok" | "caution" | "warn"; headline: string; detail: string } {
  const agent = a.setting === "snmp_agent";
  const pathCost = a.setting === "path_cost";
  const staticRoute = a.setting === "static_route";
  // Restates ADR-0511 decision 5a — a setting this build does not know is named as received, marked.
  const words = listed(WORDS, a.setting) ? WORDS[a.setting]
    : { subject: `The setting ${unrecognized(a.setting)}`, thing: "setting", set: "set", unset: "removed", wantSet: "set", wantUnset: "removed" };
  const subject = words.subject;
  const verb = a.action === "set" ? words.set : words.unset;
  if (a.verdict === "verified" && staticRoute) {
    // A static route is deliberately never saved (ADR-0434 5a): a saved route that cost the switch its
    // path to its cell would outlive a reboot, so running-config-only is the safe direction and it reverts.
    return {
      tone: "caution",
      headline: `${subject} was ${verb} for now.`,
      detail: "The switch showed the route when it was read back, in its running configuration. It is not saved to the switch's startup configuration, on purpose: a saved route that cost the switch its path to its cell would outlive a restart, so the safe direction is to keep it in running configuration only. The change reverts at the next restart.",
    };
  }
  if (a.verdict === "verified" && pathCost) {
    // Path cost is deliberately never saved (ADR-0426 8a): a spanning-tree change is saved only after
    // confirming the switch still has a path to the root bridge, and the read that check needs is refused
    // on this switch over the management channel. So a confirmed change is running-config-only by design,
    // not for want of saving, and reverts at the next restart — the safe direction.
    return {
      tone: "caution",
      headline: `${subject} was ${verb} for now.`,
      detail: "The switch showed the change when it was read back, in its running configuration. It is not saved to the switch's startup configuration, on purpose: Zenith saves a spanning-tree change only after confirming the switch still has a path to the root bridge, and that check cannot be made reliably on this switch over the management channel. The change reverts at the next restart.",
    };
  }
  if (a.verdict === "verified" && agent) {
    // The agent's enable is schema-owned: the switch's uCentral agent resets it at the next
    // reboot or configuration apply, so this is never saved and never a lasting change.
    return {
      tone: "caution",
      headline: `${subject} was ${verb} for now.`,
      detail: "The switch showed the change when it was read back, in its running configuration. It is temporary: the switch resets the SNMP service at its next reboot or configuration apply. The lasting setting comes with the switch's managed configuration.",
    };
  }
  if (a.verdict === "verified" && a.durability === "persisted") {
    return { tone: "ok", headline: `${subject} was ${verb} and saved.`, detail: `The switch showed the change when it was read back, and it was saved to the switch's startup configuration.` };
  }
  if (a.verdict === "verified") {
    return { tone: "caution", headline: `${subject} was ${verb}, but not saved.`, detail: `The switch showed the change when it was read back, and it is in the running configuration only. It will be lost at the next restart unless the configuration is saved on the switch.` };
  }
  const thing = words.thing;
  const wanted = a.action === "set" ? words.wantSet : words.wantUnset;
  return {
    tone: "warn",
    headline: "The switch did not confirm the change.",
    detail: `The instruction was sent, but the switch did not show the ${thing} ${wanted} when it was read back` +
      (a.read_back === "unreadable" ? " (its answer could not be read)" : "") +
      ". Read the setting on the switch before doing anything else. Nothing was saved.",
  };
}

/** Why a setting the read left out is drawn disabled. */
const NOT_MEASURED = "Zenith will not send this to this switch: its model and firmware have not been measured for this setting. Nothing was tried.";

export default function SwitchCliSettings({ serial, apply, lane, read }: { serial: string; apply: ApplyCliSetting; lane: ReturnType<typeof useMutation>; read?: ReadCliAdmission }) {
  const admission = useQuery(() => (read ? read(serial) : SKIP), [serial]);
  // Only an answer that arrived says anything. Pending, failed or not asked: no claim.
  const admitted = admission.status === "ready" ? new Set<string>(admission.data.admitted) : undefined;
  const communityOk = admitted === undefined || admitted.has("snmp_community");
  const agentOk = admitted === undefined || admitted.has("snmp_agent");
  const mrouterOk = admitted === undefined || admitted.has("mrouter_port");
  const pathCostOk = admitted === undefined || admitted.has("path_cost");
  const staticRouteOk = admitted === undefined || admitted.has("static_route");
  const facilityOk = admitted === undefined || admitted.has("syslog_facility");
  const lookupOk = admitted === undefined || admitted.has("dns_domain_lookup");
  const domainOk = admitted === undefined || admitted.has("dns_domain_name");
  // A direction the apply refuses on every switch, with the server's own words for why (ADR-0521).
  // Read, never listed here, and absent until the read arrives: the apply's 409 stays the gate.
  const withdrawnWhy = (setting: string, act: Request["action"]) =>
    admission.status === "ready" ? admission.data.withdrawn.find((w) => w.setting === setting && w.action === act)?.why : undefined;
  const lookupOffWhy = withdrawnWhy("dns_domain_lookup", "unset");
  const [action, setAction] = useState<Request["action"]>("set");
  const [community, setCommunity] = useState("");
  const [agentAction, setAgentAction] = useState<Request["action"]>("set");
  const [mrouterAction, setMrouterAction] = useState<Request["action"]>("set");
  const [mrouterVlan, setMrouterVlan] = useState("");
  const [mrouterPort, setMrouterPort] = useState("");
  const [pathCostAction, setPathCostAction] = useState<Request["action"]>("set");
  const [pathCostPort, setPathCostPort] = useState("");
  const [pathCostValue, setPathCostValue] = useState("");
  const [routeAction, setRouteAction] = useState<Request["action"]>("set");
  const [routePrefix, setRoutePrefix] = useState("");
  const [routeMask, setRouteMask] = useState("");
  const [routeNextHop, setRouteNextHop] = useState("");
  const [facilityAction, setFacilityAction] = useState<Request["action"]>("set");
  const [facility, setFacility] = useState(23);
  const [lookupAction, setLookupAction] = useState<Request["action"]>("set");
  const [domainAction, setDomainAction] = useState<Request["action"]>("set");
  const [domainName, setDomainName] = useState("");
  const [result, setResult] = useState<Applied>();
  const [status, setStatus] = useState<number>();
  const valid = COMMUNITY.test(community);
  const mrouterValid = mrouterVlanValid(mrouterVlan) && MROUTER_PORT.test(mrouterPort);
  // A set needs a valid port and cost; an unset needs only the port (the cost is not sent).
  const pathCostValid = PATH_COST_PORT.test(pathCostPort) && (pathCostAction === "unset" || pathCostValueValid(pathCostValue));
  // Set and unset both carry all three fields (the `no` form names the whole route). The server does the
  // exact validation (aligned prefix, contiguous mask, unicast next hop) and the reachability refusal.
  const routeValid = ROUTE_ADDR.test(routePrefix) && ROUTE_ADDR.test(routeMask) && ROUTE_ADDR.test(routeNextHop);

  // The apply's record, followed until it ends (ADR-0480 3a and 4a, #2064): on `done` its `result` is
  // what this write answered before, drawn as it always was.
  const accepted = useAcceptedApply<Applied>(setResult);

  const send = async (body: Request) => {
    setResult(undefined); setStatus(undefined); accepted.clear();
    const r = await lane.runWith(() => apply(serial, body));
    // ADR-0480 (#2061): the apply's id, handed to the shell; a refusal in the request is drawn below.
    if (accepted.take(r, `Command-line setting on ${serial}`)) return;
    setStatus(r.status);
  };

  const problem = lane.problem;
  const shown = result ? describeApplied(result) : undefined;
  return (
    <Card className="panel" title="Advanced: command-line settings"
      head={<Info label="About command-line settings">
        These are applied by the switch&rsquo;s own command line, outside the settings plan and apply above. There is
        no plan and no preview: the switch is read back afterwards and the result is shown here. Whether a change
        survives a restart is measured per switch model and firmware.
      </Info>}>
      <fieldset className="actions" disabled={!communityOk}>
        <legend>Read-only SNMP community</legend>
        <label><input type="radio" name="cli-action" checked={action === "set"} onChange={() => setAction("set")} /> Add</label>{" "}
        <label><input type="radio" name="cli-action" checked={action === "unset"} onChange={() => setAction("unset")} /> Remove</label>{" "}
        <label>Community name{" "}
          <input type="password" autoComplete="off" spellCheck={false} maxLength={32} value={community}
            aria-label="Community name" onChange={(e) => setCommunity(e.target.value)} />
        </label>{" "}
        <button type="button" className="btn" disabled={!communityOk || !valid || lane.busy} onClick={() => {
          const body: Request = { setting: "snmp_community", action, community };
          // The name is cleared as it is sent, win or lose: a retry is a fresh act.
          setCommunity("");
          void send(body);
        }}>
          {action === "set" ? "Add the community" : "Remove the community"}
        </button>
        {!communityOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
      </fieldset>
      <fieldset className="actions" disabled={!agentOk}>
        <legend>SNMP agent</legend>
        <label><input type="radio" name="cli-agent-action" checked={agentAction === "set"} onChange={() => setAgentAction("set")} /> Enable</label>{" "}
        <label><input type="radio" name="cli-agent-action" checked={agentAction === "unset"} onChange={() => setAgentAction("unset")} /> Disable</label>{" "}
        <button type="button" className="btn" disabled={!agentOk || lane.busy}
          onClick={() => void send({ setting: "snmp_agent", action: agentAction })}>
          {agentAction === "set" ? "Enable the SNMP agent" : "Disable the SNMP agent"}
        </button>
        {!agentOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
      </fieldset>
      <fieldset className="actions" disabled={!facilityOk}>
        {/* In the legend, which a disabled fieldset does not disable, so the ⓘ still opens there. */}
        <legend>Remote logging facility <Info label="About the syslog facility">The syslog facility the switch puts on what it sends, local0 to local7. The default is local7.</Info></legend>
        <label><input type="radio" name="cli-facility-action" checked={facilityAction === "set"} onChange={() => setFacilityAction("set")} /> Set a facility</label>{" "}
        <label><input type="radio" name="cli-facility-action" checked={facilityAction === "unset"} onChange={() => setFacilityAction("unset")} /> Back to the default</label>{" "}
        {facilityAction === "set" && (
          <label>Facility{" "}
            <select aria-label="Facility" value={facility} onChange={(e) => setFacility(Number(e.target.value))}>
              {[16, 17, 18, 19, 20, 21, 22, 23].map((n) => <option key={n} value={n}>local{n - 16} ({n})</option>)}
            </select>
          </label>
        )}{" "}
        <button type="button" className="btn" disabled={!facilityOk || lane.busy}
          onClick={() => void send(facilityAction === "set" ? { setting: "syslog_facility", action: "set", syslog_facility: facility } : { setting: "syslog_facility", action: "unset" })}>
          {facilityAction === "set" ? "Set the facility" : "Reset the facility"}
        </button>
        {!facilityOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
      </fieldset>
      <fieldset className="actions" disabled={!lookupOk}>
        {/* In the legend, which a disabled fieldset does not disable, so the ⓘ still opens there. */}
        <legend>DNS lookup <Info label="About DNS lookup">Whether the switch resolves names at all, for example a syslog or time server given by name.</Info></legend>
        <label><input type="radio" name="cli-lookup-action" checked={lookupAction === "set"} onChange={() => setLookupAction("set")} /> Resolve names</label>{" "}
        <label><input type="radio" name="cli-lookup-action" checked={lookupAction === "unset"} disabled={lookupOffWhy !== undefined} onChange={() => setLookupAction("unset")} /> Do not resolve names</label>{" "}
        <button type="button" className="btn" disabled={!lookupOk || lane.busy || (lookupAction === "unset" && lookupOffWhy !== undefined)}
          onClick={() => void send({ setting: "dns_domain_lookup", action: lookupAction })}>
          {lookupAction === "set" ? "Enable DNS lookup" : "Disable DNS lookup"}
        </button>
        {!lookupOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
        {/* Restates ADR-0521 decision 1a: the disable is refused on every switch. The reason is the server's. */}
        {lookupOk && lookupOffWhy !== undefined && <p className="muted" role="note">Zenith will not disable DNS lookup on any switch: {lookupOffWhy}.</p>}
      </fieldset>
      <fieldset className="actions" disabled={!domainOk}>
        <legend>Default domain name</legend>
        <label><input type="radio" name="cli-domain-action" checked={domainAction === "set"} onChange={() => setDomainAction("set")} /> Set the domain name</label>{" "}
        <label><input type="radio" name="cli-domain-action" checked={domainAction === "unset"} onChange={() => setDomainAction("unset")} /> Clear the domain name</label>{" "}
        {domainAction === "set" && (
          <label>Domain name{" "}
            <input type="text" autoComplete="off" spellCheck={false} maxLength={63} placeholder="example.com" value={domainName}
              aria-label="Domain name" onChange={(e) => setDomainName(e.target.value.trim())} />
          </label>
        )}{" "}
        <button type="button" className="btn" disabled={!domainOk || (domainAction === "set" && !domainNameValid(domainName)) || lane.busy}
          onClick={() => void send(domainAction === "set" ? { setting: "dns_domain_name", action: "set", domain_name: domainName } : { setting: "dns_domain_name", action: "unset" })}>
          {domainAction === "set" ? "Set the domain name" : "Clear the domain name"}
        </button>
        {!domainOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
      </fieldset>
      <fieldset className="actions" disabled={!mrouterOk}>
        {/* In the legend, which a disabled fieldset does not disable, so the ⓘ still opens there. */}
        <legend>Static multicast-router port <Info label="About static multicast-router ports">Designates a port as a static IGMP multicast-router port for one VLAN. It changes no port&rsquo;s admin state, VLAN membership or spanning tree, so it cannot take the switch off the air.</Info></legend>
        <label><input type="radio" name="cli-mrouter-action" checked={mrouterAction === "set"} onChange={() => setMrouterAction("set")} /> Add a mrouter port</label>{" "}
        <label><input type="radio" name="cli-mrouter-action" checked={mrouterAction === "unset"} onChange={() => setMrouterAction("unset")} /> Remove a mrouter port</label>{" "}
        <label>Mrouter VLAN{" "}
          <input type="text" inputMode="numeric" autoComplete="off" spellCheck={false} maxLength={4} placeholder="101" value={mrouterVlan}
            onChange={(e) => setMrouterVlan(e.target.value.trim())} />
        </label>{" "}
        <label>Mrouter port{" "}
          <input type="text" autoComplete="off" spellCheck={false} maxLength={4} placeholder="1/3" value={mrouterPort}
            onChange={(e) => setMrouterPort(e.target.value.trim())} />
        </label>{" "}
        <button type="button" className="btn" disabled={!mrouterOk || !mrouterValid || lane.busy}
          onClick={() => void send({ setting: "mrouter_port", action: mrouterAction, mrouter_vlan: Number(mrouterVlan), mrouter_port: mrouterPort })}>
          {mrouterAction === "set" ? "Add the mrouter port" : "Remove the mrouter port"}
        </button>
        {!mrouterOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
      </fieldset>
      <fieldset className="actions" disabled={!pathCostOk}>
        <legend>Spanning-tree path cost</legend>
        <p className="hint" role="note">{PATH_COST_NOTE}</p>
        <label><input type="radio" name="cli-path-cost-action" checked={pathCostAction === "set"} onChange={() => setPathCostAction("set")} /> Set a path cost</label>{" "}
        <label><input type="radio" name="cli-path-cost-action" checked={pathCostAction === "unset"} onChange={() => setPathCostAction("unset")} /> Remove the path cost</label>{" "}
        <label>Path-cost port{" "}
          <input type="text" autoComplete="off" spellCheck={false} maxLength={4} placeholder="1/4" value={pathCostPort}
            aria-label="Path-cost port" onChange={(e) => setPathCostPort(e.target.value.trim())} />
        </label>{" "}
        {pathCostAction === "set" && (
          <label>Path cost{" "}
            <input type="text" inputMode="numeric" autoComplete="off" spellCheck={false} maxLength={9} placeholder="2000" value={pathCostValue}
              aria-label="Path cost" onChange={(e) => setPathCostValue(e.target.value.trim())} />
          </label>
        )}{" "}
        <button type="button" className="btn" disabled={!pathCostOk || !pathCostValid || lane.busy}
          onClick={() => void send(pathCostAction === "set"
            ? { setting: "path_cost", action: "set", path_cost_port: pathCostPort, path_cost: Number(pathCostValue) }
            : { setting: "path_cost", action: "unset", path_cost_port: pathCostPort })}>
          {pathCostAction === "set" ? "Set the path cost" : "Remove the path cost"}
        </button>
        {!pathCostOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
      </fieldset>
      <fieldset className="actions" disabled={!staticRouteOk}>
        <legend>Static route</legend>
        <p className="hint" role="note">{STATIC_ROUTE_NOTE}</p>
        <label><input type="radio" name="cli-route-action" checked={routeAction === "set"} onChange={() => setRouteAction("set")} /> Add a route</label>{" "}
        <label><input type="radio" name="cli-route-action" checked={routeAction === "unset"} onChange={() => setRouteAction("unset")} /> Remove a route</label>{" "}
        <label>Destination network{" "}
          <input type="text" inputMode="decimal" autoComplete="off" spellCheck={false} maxLength={15} placeholder="192.0.2.0" value={routePrefix}
            aria-label="Route destination network" onChange={(e) => setRoutePrefix(e.target.value.trim())} />
        </label>{" "}
        <label>Mask{" "}
          <input type="text" inputMode="decimal" autoComplete="off" spellCheck={false} maxLength={15} placeholder="255.255.255.0" value={routeMask}
            aria-label="Route mask" onChange={(e) => setRouteMask(e.target.value.trim())} />
        </label>{" "}
        <label>Next hop{" "}
          <input type="text" inputMode="decimal" autoComplete="off" spellCheck={false} maxLength={15} placeholder="192.0.2.1" value={routeNextHop}
            aria-label="Route next hop" onChange={(e) => setRouteNextHop(e.target.value.trim())} />
        </label>{" "}
        <button type="button" className="btn" disabled={!staticRouteOk || !routeValid || lane.busy}
          onClick={() => void send({ setting: "static_route", action: routeAction, route_prefix: routePrefix, route_mask: routeMask, route_next_hop: routeNextHop })}>
          {routeAction === "set" ? "Add the route" : "Remove the route"}
        </button>
        {!staticRouteOk && <p className="muted" role="note">{NOT_MEASURED}</p>}
      </fieldset>
      {(pathCostPort !== "" || (pathCostAction === "set" && pathCostValue !== "")) && !pathCostValid && <p className="muted" role="note">A path cost is a port in the form 1/&lt;n&gt; and, to set one, a cost from 1 to 200000000, for example port 1/4 cost 2000.</p>}
      {(routePrefix !== "" || routeMask !== "" || routeNextHop !== "") && !routeValid && <p className="muted" role="note">A static route is a destination network, a mask and a next hop, each an IPv4 address, for example 192.0.2.0 255.255.255.0 192.0.2.1.</p>}
      {(mrouterVlan !== "" || mrouterPort !== "") && !mrouterValid && <p className="muted" role="note">A mrouter port is a VLAN id (1&ndash;4094) and a port in the form 1/&lt;n&gt;, for example VLAN 101 port 1/3.</p>}
      {domainAction === "set" && domainName !== "" && !domainNameValid(domainName) && <p className="muted" role="note">A domain name is up to 63 lowercase letters, digits, hyphens and dots, for example example.com.</p>}
      {community !== "" && !valid && <p className="muted" role="note">A community name is 1 to 32 letters, digits, hyphens or underscores.</p>}
      {shown && result && (
        <div role="status" className={`notice notice-${shown.tone}`}>
          <p><strong>{shown.headline}</strong></p>
          <p>{shown.detail}</p>
          {result.cli_refused && <p>The switch&rsquo;s command line refused at least one instruction in the script.</p>}
        </div>
      )}
      {/* Restates ADR-0480 decisions 2a and 3a — the write is accepted before the switch is asked whether it
          is online or sent anything; a refusal from that read, and the verdict, are the apply's record. */}
      <AcceptedApplyNotice waiting={accepted.waiting} ended={accepted.ended} />
      {accepted.ended?.record?.state === "unknown" && <p><strong>{NO_ANSWER}</strong></p>}
      {problem && <Refusal problem={problem} status={status} onDismiss={lane.clear} />}
    </Card>
  );
}

/** What a write whose answer never came back leaves the operator to do: its record ends `unknown`
 * (ADR-0480 decision 6a), and the switch may hold the change. */
const NO_ANSWER = "The switch did not answer in time, so the change may or may not have been made. Read the setting on the switch before asking again; sending it again is a second write.";

function Refusal({ problem, status, onDismiss }: { problem: NonNullable<ReturnType<typeof useMutation>["problem"]>; status?: number; onDismiss(): void }) {
  let lead: string | undefined;
  if (status === 409) lead = "Not applied, and nothing was sent. This device cannot take this setting from here as things stand: it is not a switch, its controller no longer has it, or another apply to it is still running.";
  else if (problem.reason === "outcome_unknown") lead = NO_ANSWER;
  else if (problem.reason === "rejected") lead = "The controller or the switch refused the instruction. It was not applied, and sending it again will not change that.";
  return (
    <div>
      {lead && <p><strong>{lead}</strong></p>}
      <ProblemBox problem={problem} onDismiss={onDismiss} />
    </div>
  );
}
